import React, { Suspense, lazy } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { ThemeProvider } from "./context/ThemeContext";
import Layout from "./components/Layout";
import ErrorBoundary from "./components/ErrorBoundary";
import { useQueryClient } from "@tanstack/react-query";
import { warmGlobalCache } from "./lib/queries";

// Login / Register / public auth pages stay eager — they're tiny, no charts,
// and are the FIRST screen new visitors see, so we don't want a Suspense flash.
import Login from "./pages/Login";
import Register from "./pages/Register";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import CreatePassword from "./pages/CreatePassword";
import AthleteInvite from "./pages/AthleteInvite";
import ParentInvite from "./pages/ParentInvite";

// Authenticated routes — lazy-load so the initial bundle stays small.
// Each page becomes its own JS chunk that downloads only when the user
// actually navigates to it. Cuts initial JS by ~60-70%.
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Athletes = lazy(() => import("./pages/Athletes"));
const AthleteDetail = lazy(() => import("./pages/AthleteDetail"));
const Quadrant = lazy(() => import("./pages/Quadrant"));
const QuadrantScore = lazy(() => import("./pages/QuadrantScore"));
const ProfileSettings = lazy(() => import("./pages/ProfileSettings"));
const BrandingSettings = lazy(() => import("./pages/BrandingSettings"));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const Import = lazy(() => import("./pages/Import"));
const VideoBreakdown = lazy(() => import("./pages/VideoBreakdown"));
const TestingSession = lazy(() => import("./pages/TestingSession"));
const TestLibrary = lazy(() => import("./pages/TestLibrary"));
const Standards = lazy(() => import("./pages/Standards"));
const Groups = lazy(() => import("./pages/Groups"));
const Pricing = lazy(() => import("./pages/Pricing"));
const BillingSuccess = lazy(() => import("./pages/BillingSuccess"));
const IssueLibrary = lazy(() => import("./pages/IssueLibrary"));
const Programs = lazy(() => import("./pages/Programs"));
const ProgramBuilder = lazy(() => import("./pages/ProgramBuilder"));
const CoachSessionTracker = lazy(() => import("./pages/CoachSessionTracker"));
const CoachCalendar = lazy(() => import("./pages/CoachCalendar"));
const Readiness = lazy(() => import("./pages/Readiness"));
const AthleteWorkouts = lazy(() => import("./pages/AthleteWorkouts"));
const TeamWorkouts = lazy(() => import("./pages/TeamWorkouts"));
const MacroOverview = lazy(() => import("./pages/MacroOverview"));
const MacroTemplates = lazy(() => import("./pages/MacroTemplates"));
const MacroTemplateEditor = lazy(() => import("./pages/MacroTemplateEditor"));
const TeamDetail = lazy(() => import("./pages/TeamDetail"));
const ExerciseLibrary = lazy(() => import("./pages/ExerciseLibrary"));
const BulkUpdateNotes = lazy(() => import("./pages/BulkUpdateNotes"));
const OnlinePrograms = lazy(() => import("./pages/OnlinePrograms"));
const More = lazy(() => import("./pages/More"));
const MyResources = lazy(() => import("./pages/MyResources"));
const AIProgramGenerator = lazy(() => import("./pages/AIProgramGenerator"));
const AdaptFrequency = lazy(() => import("./pages/AdaptFrequency"));
const MyOnlineWorkouts = lazy(() => import("./pages/MyOnlineWorkouts"));
const AthleteWorkoutPlayer = lazy(() => import("./pages/AthleteWorkoutPlayer"));
const HealthQuestions = lazy(() => import("./pages/HealthQuestions"));
const LookupTables = lazy(() => import("./pages/LookupTables"));
const MyHealthQuestionnaire = lazy(() => import("./pages/MyHealthQuestionnaire"));
const AthleteIntake = lazy(() => import("./pages/AthleteIntake"));
const AppearanceSettings = lazy(() => import("./pages/AppearanceSettings"));
const AccountSecurity = lazy(() => import("./pages/AccountSecurity"));
const HelpFeedback = lazy(() => import("./pages/HelpFeedback"));

function Protected({ children }) {
  const { user, ready } = useAuth();
  const qc = useQueryClient();
  // Background cache warm-up: as soon as the user is authenticated, pre-fetch
  // the data every page needs (roster, teams, test defs). Zero penalty to the
  // current page's paint — these run alongside, not before.
  React.useEffect(() => {
    if (ready && user) warmGlobalCache(qc);
    // Only re-warm when identity actually changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, user?.id]);
  if (!ready) return <AppBootScreen />;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function AppBootScreen() {
  // Nicer initial-load screen — small pulse logo + purple ring so the very
  // first paint feels intentional instead of a plain "initializing" line.
  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white flex flex-col items-center justify-center gap-4" data-testid="app-boot">
      <div className="relative w-16 h-16">
        <div className="absolute inset-0 rounded-full border-2 border-white/10" />
        <div className="absolute inset-0 rounded-full border-2 border-transparent border-t-[#7C3AED] border-r-[#7C3AED] animate-spin" />
        <div className="absolute inset-2.5 rounded-full bg-gradient-to-br from-[#7C3AED] to-[#5B21B6] flex items-center justify-center font-heading font-black text-white text-xl">R</div>
      </div>
      <div className="text-[10px] uppercase tracking-[0.32em] text-[#A78BFA] font-bold">RD SmartMetrics</div>
      <div className="text-[10px] uppercase tracking-[0.28em] text-[#52525B] flex items-center gap-2">
        <span className="w-1 h-1 bg-[#7C3AED] rounded-full animate-pulse" />
        Loading your workspace…
      </div>
    </div>
  );
}

function CoachOnly({ children }) {
  const { user, ready } = useAuth();
  if (!ready) return null;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === "athlete") return <Navigate to="/" replace />;
  return children;
}

function SuperAdminOnly({ children }) {
  const { user, ready } = useAuth();
  if (!ready) return null;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "super_admin") return <Navigate to="/" replace />;
  return children;
}

// Lightweight skeleton shown while a lazy route's chunk is downloading.
// Matches the dark aesthetic so it doesn't flash white.
function RouteFallback() {
  return (
    <div className="p-6 lg:p-8 space-y-4" data-testid="route-loading">
      {/* Header shape */}
      <div className="space-y-2">
        <div className="h-3 w-24 bg-white/[0.06] rounded animate-pulse" />
        <div className="h-8 w-72 bg-white/[0.08] rounded animate-pulse" />
      </div>
      {/* Content grid shape */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="bg-[#121214] border border-white/10 rounded-xl p-4 animate-pulse space-y-3" style={{ animationDelay: `${i * 80}ms` }}>
            <div className="h-8 w-8 bg-white/[0.06] rounded-md" />
            <div className="h-3 w-24 bg-white/[0.06] rounded" />
            <div className="h-7 w-16 bg-white/[0.08] rounded" />
          </div>
        ))}
      </div>
      <div className="bg-[#121214] border border-white/10 rounded-xl h-64 animate-pulse" />
    </div>
  );
}

export default function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <ThemeProvider>
          <Suspense fallback={<RouteFallback />}>
            <Routes>
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/create-password/:token" element={<CreatePassword />} />
              <Route path="/invite/:token" element={<AthleteInvite />} />
              <Route path="/parent-invite/:token" element={<ParentInvite />} />
              <Route path="/billing/success" element={<Protected><BillingSuccess /></Protected>} />
              <Route element={<Protected><Layout /></Protected>}>
                <Route index element={<ErrorBoundary label="Dashboard"><Dashboard /></ErrorBoundary>} />
                <Route path="pricing" element={<SuperAdminOnly><ErrorBoundary label="Pricing"><Pricing /></ErrorBoundary></SuperAdminOnly>} />
                <Route path="athletes" element={<ErrorBoundary label="Athletes"><Athletes /></ErrorBoundary>} />
                <Route path="athletes/:id" element={<ErrorBoundary label="Athlete profile"><AthleteDetail /></ErrorBoundary>} />
                <Route path="quadrant" element={<ErrorBoundary label="Quadrant"><Quadrant /></ErrorBoundary>} />
                <Route path="quadrant-score" element={<CoachOnly><ErrorBoundary label="Quadrant Score"><QuadrantScore /></ErrorBoundary></CoachOnly>} />
                <Route path="profile" element={<ErrorBoundary label="Profile"><ProfileSettings /></ErrorBoundary>} />
                <Route path="settings/appearance" element={<ErrorBoundary label="Appearance"><AppearanceSettings /></ErrorBoundary>} />
                <Route path="settings/security" element={<ErrorBoundary label="Account & Security"><AccountSecurity /></ErrorBoundary>} />
                <Route path="help" element={<ErrorBoundary label="Help"><HelpFeedback /></ErrorBoundary>} />
                <Route path="settings/branding" element={<CoachOnly><ErrorBoundary label="Branding"><BrandingSettings /></ErrorBoundary></CoachOnly>} />
                <Route path="admin" element={<SuperAdminOnly><ErrorBoundary label="Admin"><AdminDashboard /></ErrorBoundary></SuperAdminOnly>} />
                <Route path="test-library" element={<CoachOnly><ErrorBoundary label="Test library"><TestLibrary /></ErrorBoundary></CoachOnly>} />
                <Route path="issue-library" element={<CoachOnly><ErrorBoundary label="Issue library"><IssueLibrary /></ErrorBoundary></CoachOnly>} />
                <Route path="programs" element={<CoachOnly><ErrorBoundary label="Programs"><Programs /></ErrorBoundary></CoachOnly>} />
                <Route path="programs/:id" element={<CoachOnly><ErrorBoundary label="Program builder"><ProgramBuilder /></ErrorBoundary></CoachOnly>} />
                <Route path="adapt-frequency" element={<CoachOnly><ErrorBoundary label="Adapt frequency"><AdaptFrequency /></ErrorBoundary></CoachOnly>} />
                <Route path="programs/:pid/track" element={<CoachOnly><ErrorBoundary label="Session tracker"><CoachSessionTracker /></ErrorBoundary></CoachOnly>} />
                <Route path="coach-calendar" element={<CoachOnly><ErrorBoundary label="Calendar"><CoachCalendar /></ErrorBoundary></CoachOnly>} />
                <Route path="readiness" element={<CoachOnly><ErrorBoundary label="Readiness"><Readiness /></ErrorBoundary></CoachOnly>} />
                <Route path="athlete-workouts" element={<CoachOnly><ErrorBoundary label="Athlete workouts"><AthleteWorkouts /></ErrorBoundary></CoachOnly>} />
                <Route path="team-workouts" element={<CoachOnly><ErrorBoundary label="Team workouts"><TeamWorkouts /></ErrorBoundary></CoachOnly>} />
                <Route path="team-workouts/:name" element={<CoachOnly><ErrorBoundary label="Team workouts"><TeamWorkouts /></ErrorBoundary></CoachOnly>} />
                <Route path="macro-overview" element={<ErrorBoundary label="Macro overview"><MacroOverview /></ErrorBoundary>} />
                <Route path="macro-templates" element={<CoachOnly><ErrorBoundary label="Macro templates"><MacroTemplates /></ErrorBoundary></CoachOnly>} />
                <Route path="macro-templates/new" element={<CoachOnly><ErrorBoundary label="Macro editor"><MacroTemplateEditor /></ErrorBoundary></CoachOnly>} />
                <Route path="macro-templates/:tid/edit" element={<CoachOnly><ErrorBoundary label="Macro editor"><MacroTemplateEditor /></ErrorBoundary></CoachOnly>} />
                <Route path="exercise-library" element={<CoachOnly><ErrorBoundary label="Exercise library"><ExerciseLibrary /></ErrorBoundary></CoachOnly>} />
                <Route path="exercise-library/bulk-update-notes" element={<CoachOnly><ErrorBoundary label="Bulk update notes"><BulkUpdateNotes /></ErrorBoundary></CoachOnly>} />
                <Route path="online-programs" element={<CoachOnly><ErrorBoundary label="Online programs"><OnlinePrograms /></ErrorBoundary></CoachOnly>} />
                <Route path="online-programs/ai-generator" element={<CoachOnly><ErrorBoundary label="AI generator"><AIProgramGenerator /></ErrorBoundary></CoachOnly>} />
                <Route path="my-workouts" element={<ErrorBoundary label="My workouts"><MyOnlineWorkouts /></ErrorBoundary>} />
                <Route path="more" element={<ErrorBoundary label="More"><More /></ErrorBoundary>} />
                <Route path="resources" element={<ErrorBoundary label="Resources"><MyResources /></ErrorBoundary>} />
                <Route path="my-workouts/:id" element={<ErrorBoundary label="Workout player"><AthleteWorkoutPlayer /></ErrorBoundary>} />
                <Route path="health-questions" element={<CoachOnly><ErrorBoundary label="Health questions"><HealthQuestions /></ErrorBoundary></CoachOnly>} />
                <Route path="lookup-tables" element={<CoachOnly><ErrorBoundary label="Lookup tables"><LookupTables /></ErrorBoundary></CoachOnly>} />
                <Route path="my-health" element={<ErrorBoundary label="My health"><MyHealthQuestionnaire /></ErrorBoundary>} />
                <Route path="intakes/new" element={<CoachOnly><ErrorBoundary label="New intake"><AthleteIntake /></ErrorBoundary></CoachOnly>} />
                <Route path="intakes/:id" element={<CoachOnly><ErrorBoundary label="Intake"><AthleteIntake /></ErrorBoundary></CoachOnly>} />
                <Route path="standards" element={<CoachOnly><ErrorBoundary label="Standards"><Standards /></ErrorBoundary></CoachOnly>} />
                <Route path="groups" element={<CoachOnly><ErrorBoundary label="Groups"><Groups /></ErrorBoundary></CoachOnly>} />
                <Route path="teams/:name" element={<CoachOnly><ErrorBoundary label="Team"><TeamDetail /></ErrorBoundary></CoachOnly>} />
                <Route path="testing" element={<CoachOnly><ErrorBoundary label="Testing session"><TestingSession /></ErrorBoundary></CoachOnly>} />
                <Route path="video-breakdown" element={<ErrorBoundary label="Video breakdown"><VideoBreakdown /></ErrorBoundary>} />
                <Route path="sprint-analysis" element={<ErrorBoundary label="Sprint analysis"><VideoBreakdown /></ErrorBoundary>} />
                <Route path="jump-analysis" element={<ErrorBoundary label="Jump analysis"><VideoBreakdown /></ErrorBoundary>} />
                <Route path="import" element={<ErrorBoundary label="Import"><Import /></ErrorBoundary>} />
              </Route>
              <Route path="*" element={<Navigate to="/" />} />
            </Routes>
          </Suspense>
          </ThemeProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}
